import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Bell, Star, User, BookOpen, Calendar, Eye, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';

const NotificationsPage = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [newCalls, setNewCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('new-calls'); // 'new-calls' or 'notifications'

  useEffect(() => {
    fetchData();
    // Mark calls as seen when page loads
    markCallsAsSeen();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch both notifications and new calls in parallel
      const [notifRes, callsRes] = await Promise.all([
        api.get('/api/notifications'),
        api.get('/api/calls?days=7&limit=20') // Get recent calls
      ]);
      
      setNotifications(notifRes.data.notifications || []);
      setNewCalls(callsRes.data.calls || []);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const markCallsAsSeen = async () => {
    try {
      await api.post('/api/notifications/mark-calls-seen');
    } catch (error) {
      console.error('Error marking calls as seen');
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await api.post(`/api/notifications/${notificationId}/read`);
      setNotifications(notifications.map(n => 
        n.notification_id === notificationId ? { ...n, read: true } : n
      ));
    } catch (error) {
      toast.error('Erro ao marcar como lida');
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString('pt-BR');
  };

  const formatShortDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Hoje';
    if (diffDays === 1) return 'Ontem';
    if (diffDays < 7) return `${diffDays} dias atrás`;
    return date.toLocaleDateString('pt-BR');
  };

  const getQualisClass = (qualis) => {
    const colors = {
      'A1': 'bg-green-100 text-green-800 border-green-200',
      'A2': 'bg-green-50 text-green-700 border-green-200',
      'A3': 'bg-blue-100 text-blue-800 border-blue-200',
      'A4': 'bg-blue-50 text-blue-700 border-blue-200',
      'B1': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'B2': 'bg-yellow-50 text-yellow-700 border-yellow-200',
      'B3': 'bg-orange-100 text-orange-800 border-orange-200',
      'B4': 'bg-orange-50 text-orange-700 border-orange-200',
      'B5': 'bg-red-50 text-red-700 border-red-200',
      'C': 'bg-gray-100 text-gray-700 border-gray-200'
    };
    return colors[qualis] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="min-h-screen bg-[#F9F9F8]">
      <header className="bg-white border-b border-[#E4E4E7]">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              data-testid="back-btn"
              variant="ghost"
              size="icon"
              onClick={() => navigate('/dashboard')}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-2xl font-bold tracking-tight">Notificações</h1>
          </div>
          <div className="flex items-center gap-4">
            <Button
              data-testid="favorites-btn"
              variant="ghost"
              size="icon"
              onClick={() => navigate('/favorites')}
            >
              <Star className="h-5 w-5" />
            </Button>
            <Button
              data-testid="profile-btn"
              variant="ghost"
              size="icon"
              onClick={() => navigate('/profile')}
            >
              <User className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <Button
            variant={activeTab === 'new-calls' ? 'default' : 'outline'}
            onClick={() => setActiveTab('new-calls')}
            className={activeTab === 'new-calls' ? 'bg-[#002FA7]' : ''}
          >
            <BookOpen className="h-4 w-4 mr-2" />
            Novas Chamadas
            {newCalls.length > 0 && (
              <span className="ml-2 bg-white/20 px-2 py-0.5 rounded-full text-xs">
                {newCalls.length}
              </span>
            )}
          </Button>
          <Button
            variant={activeTab === 'notifications' ? 'default' : 'outline'}
            onClick={() => setActiveTab('notifications')}
            className={activeTab === 'notifications' ? 'bg-[#002FA7]' : ''}
          >
            <Bell className="h-4 w-4 mr-2" />
            Avisos
            {notifications.filter(n => !n.read).length > 0 && (
              <span className="ml-2 bg-white/20 px-2 py-0.5 rounded-full text-xs">
                {notifications.filter(n => !n.read).length}
              </span>
            )}
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-12">Carregando...</div>
        ) : activeTab === 'new-calls' ? (
          /* New Calls Tab */
          newCalls.length === 0 ? (
            <div className="bg-white border border-[#E4E4E7] rounded-md p-12 text-center">
              <BookOpen className="h-16 w-16 mx-auto text-[#4B4C52] mb-4" />
              <p className="text-[#4B4C52]">Nenhuma chamada nova nos últimos 7 dias</p>
              <p className="text-sm text-gray-400 mt-2">
                Configure suas áreas de interesse no perfil para receber notificações relevantes
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-gray-500 mb-4">
                Chamadas publicadas nos últimos 7 dias
              </p>
              {newCalls.map(call => (
                <div
                  key={call.call_id}
                  data-testid={`new-call-${call.call_id}`}
                  className="bg-white border border-[#E4E4E7] rounded-md p-4 hover:border-[#002FA7] transition-colors cursor-pointer"
                  onClick={() => navigate(`/call/${call.call_id}`)}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className={`${getQualisClass(call.qualis_classification)} text-xs`}>
                          {call.qualis_classification}
                        </Badge>
                        <span className="text-xs text-gray-400">
                          {formatShortDate(call.created_at)}
                        </span>
                      </div>
                      <h3 className="font-semibold text-gray-900 mb-1 line-clamp-1">
                        {call.title}
                      </h3>
                      <p className="text-sm text-gray-500 mb-2">
                        {call.journal_name}
                      </p>
                      <div className="flex flex-wrap gap-2 text-xs text-gray-400">
                        {call.deadline && !call.continuous_flow && (
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            Prazo: {new Date(call.deadline).toLocaleDateString('pt-BR')}
                          </span>
                        )}
                        {call.continuous_flow && (
                          <span className="text-blue-600">Fluxo Contínuo</span>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="flex-shrink-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/call/${call.call_id}`);
                      }}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )
        ) : (
          /* Notifications Tab */
          notifications.length === 0 ? (
            <div className="bg-white border border-[#E4E4E7] rounded-md p-12 text-center">
              <Bell className="h-16 w-16 mx-auto text-[#4B4C52] mb-4" />
              <p className="text-[#4B4C52]">Nenhum aviso</p>
            </div>
          ) : (
            <div className="space-y-3">
              {notifications.map(notification => (
                <div
                  key={notification.notification_id}
                  data-testid={`notification-${notification.notification_id}`}
                  className={`bg-white border rounded-md p-4 flex items-start justify-between ${
                    notification.read ? 'border-[#E4E4E7]' : 'border-[#002FA7] bg-blue-50/30'
                  }`}
                >
                  <div className="flex-1">
                    <p className="text-sm mb-2">{notification.message}</p>
                    <p className="text-xs text-[#4B4C52]">{formatDate(notification.created_at)}</p>
                  </div>
                  {!notification.read && (
                    <Button
                      data-testid={`mark-read-btn-${notification.notification_id}`}
                      size="sm"
                      variant="ghost"
                      onClick={() => markAsRead(notification.notification_id)}
                    >
                      Marcar como lida
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default NotificationsPage;